/**
 * Image Context
 * Provides image file and preview state for upload and processing
 */
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import {
  ENTRY_SOURCE,
  readEditorEntrySession,
  readPassportEntryFromSession,
  readResizeEntryFromSession,
  writePassportEntrySession,
  writeResizeEntrySession,
  writeTrendingEntrySession,
  clearEditorEntrySession,
  isResizeEntrySource,
  isTrendingEntrySource,
} from "../utils/editorEntrySession";
import { getUploadSizeError } from "../utils/uploadLimits";

const ImageContext = createContext(undefined);

function initialEntryFromSession() {
  const session = readEditorEntrySession();
  if (!session) {
    return {
      entrySource: ENTRY_SOURCE.NORMAL,
      passportPreset: null,
      entryResizePreset: null,
    };
  }
  if (session.entrySource === ENTRY_SOURCE.PASSPORT && session.passportPreset) {
    return {
      entrySource: ENTRY_SOURCE.PASSPORT,
      passportPreset: session.passportPreset,
      entryResizePreset: null,
    };
  }
  if (isResizeEntrySource(session.entrySource) && session.resizePreset) {
    return {
      entrySource: session.entrySource,
      passportPreset: null,
      entryResizePreset: session.resizePreset,
    };
  }
  if (isTrendingEntrySource(session.entrySource) && session.resizePreset) {
    return {
      entrySource: session.entrySource,
      passportPreset: null,
      entryResizePreset: session.resizePreset,
    };
  }
  return {
    entrySource: ENTRY_SOURCE.NORMAL,
    passportPreset: null,
    entryResizePreset: null,
  };
}

export function ImageProvider({ children }) {
  const savedEntry = initialEntryFromSession();

  const [selectedFile, setSelectedFile] = useState(null);
  const [inputPreview, setInputPreview] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [entrySource, setEntrySource] = useState(savedEntry.entrySource);
  const [passportPreset, setPassportPreset] = useState(savedEntry.passportPreset);
  const [entryResizePreset, setEntryResizePreset] = useState(savedEntry.entryResizePreset);

  /** @deprecated use entrySource === ENTRY_SOURCE.PASSPORT */
  const editorFlow = entrySource === ENTRY_SOURCE.PASSPORT ? "passport" : "standard";

  const clearImage = useCallback(() => {
    setInputPreview((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    setSelectedFile(null);
  }, []);

  const clearEditorFlow = useCallback(() => {
    setEntrySource(ENTRY_SOURCE.NORMAL);
    setPassportPreset(null);
    setEntryResizePreset(null);
    clearEditorEntrySession();
  }, []);

  const setNormalEntry = useCallback(() => {
    setEntrySource(ENTRY_SOURCE.NORMAL);
    setPassportPreset(null);
    setEntryResizePreset(null);
    clearEditorEntrySession();
  }, []);

  const setFile = useCallback((file) => {
    if (file) {
      const sizeError = getUploadSizeError(file);
      if (sizeError) {
        throw new Error(sizeError);
      }
    }
    setInputPreview((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return file ? URL.createObjectURL(file) : null;
    });
    setSelectedFile(file);
    setOriginalFileName(file?.name ?? null);
  }, []);

  const startPassportFlow = useCallback((preset) => {
    setEntrySource(ENTRY_SOURCE.PASSPORT);
    setPassportPreset(preset || null);
    setEntryResizePreset(null);
    if (preset) {
      writePassportEntrySession(preset);
    }
  }, []);

  const startResizeEntryFlow = useCallback((preset, source) => {
    if (!preset || !isResizeEntrySource(source)) return;
    setEntrySource(source);
    setEntryResizePreset(preset);
    setPassportPreset(null);
    writeResizeEntrySession(source, preset);
  }, []);

  const startTrendingEntryFlow = useCallback((preset, source) => {
    if (!preset || !isTrendingEntrySource(source)) return;
    setEntrySource(source);
    setEntryResizePreset(preset);
    setPassportPreset(null);
    writeTrendingEntrySession(source, preset);
  }, []);

  useEffect(() => {
    if (entrySource === ENTRY_SOURCE.PASSPORT && passportPreset) {
      writePassportEntrySession(passportPreset);
    } else if (isResizeEntrySource(entrySource) && entryResizePreset) {
      writeResizeEntrySession(entrySource, entryResizePreset);
    } else if (isTrendingEntrySource(entrySource) && entryResizePreset) {
      writeTrendingEntrySession(entrySource, entryResizePreset);
    }
  }, [entrySource, passportPreset, entryResizePreset]);

  const value = {
    selectedFile,
    inputPreview,
    originalFileName,
    setFile,
    clearImage,
    entrySource,
    editorFlow,
    passportPreset,
    entryResizePreset,
    setEntrySource,
    setPassportPreset,
    setEntryResizePreset,
    startPassportFlow,
    startResizeEntryFlow,
    startTrendingEntryFlow,
    clearEditorFlow,
    setNormalEntry,
  };

  return (
    <ImageContext.Provider value={value}>
      {children}
    </ImageContext.Provider>
  );
}

export function useImageContext() {
  const context = useContext(ImageContext);

  if (context === undefined) {
    throw new Error("useImageContext must be used within ImageProvider");
  }

  return context;
}

export { readPassportEntryFromSession, readResizeEntryFromSession };
